/**
 * What happens on a 409. The rule (task brief): never lose what the person
 * wrote. A `VersionConflict` (packages/domain/src/kernel.ts) carries
 * `expectedVersion` and `actualVersion` in its JSON body — this module reads
 * that out of an `ApiError` and describes the two things the person can do
 * next, without touching the DOM or the network itself (both stay in the
 * screen component, this stays testable in isolation).
 */
import { ApiError } from '../api/client.js'

export interface VersionConflictInfo {
  readonly expectedVersion: number
  readonly actualVersion: number
}

/** `null` when `error` is not a 409 version conflict — anything else propagates to the generic error path. */
export function parseVersionConflict(error: unknown): VersionConflictInfo | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const { expectedVersion, actualVersion } = error.body
  if (typeof expectedVersion !== 'number' || typeof actualVersion !== 'number') return null
  return { expectedVersion, actualVersion }
}

export interface PendingEdit {
  readonly path: string
  readonly value: unknown
  readonly reason: string
  readonly expectedVersion: number
}

/**
 * "Tieni la mia e riprova": the person's edit is unchanged, only the version
 * it targets moves to the one the server just reported. The caller resends
 * this with a fresh `Idempotency-Key`, since it is a new attempt, not a
 * replay of the failed one.
 */
export function retryWithServerVersion(pending: PendingEdit, conflict: VersionConflictInfo): PendingEdit {
  return { ...pending, expectedVersion: conflict.actualVersion }
}
